import { Button, Form, Input, Modal, Select, Typography } from "antd";
import { Formik } from "formik";
import { useState } from "react";
import { useNotification } from "../../../../Hooks/useNotification";

import { allowedDomainsAll } from "../../../../Utils/Constant";

/** What the form collected, recipients split into lists. */
export interface OutgoingMail {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  message: string;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  /** Sends it; "Sent Successful" only follows once this resolves. */
  onSend: (mail: OutgoingMail) => Promise<unknown>;
  onSent: () => void;
}

const listOf = (values: string[]) => values.map((entry) => entry.trim()).filter(Boolean);

interface SendFormValues {
  emailIds: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  message: string;
}

const initialValues: SendFormValues = {
  emailIds: [],
  cc: [],
  bcc: [],
  subject: "",
  message: "",
};

/* As e-POD's Send Document. */
const MESSAGE_LIMIT = 5000;

/**
 * "Send Document" — emails the work order to one or more recipients.
 *
 * Email ID(s) / Cc / Bcc take any number of addresses, one tag each (Enter,
 * comma, semicolon or space adds one), as e-POD's Send does. Each address is
 * checked against `allowedDomainsAll` from the runtime config, the same
 * restriction the SPA onboarding form applies to approver/watcher emails —
 * so this cannot be used to mail the document outside the org.
 */
export const SendDocumentModal = ({ open, onCancel, onSend, onSent }: Props) => {
  const { openNotification } = useNotification();
  const [isSending, setIsSending] = useState(false);
  /** Validate an address list; returns an error or undefined. */
  const validateList = (value: string[], label: string, required: boolean) => {
    const list = listOf(value);

    if (!list.length) {
      return required ? `${label} is required` : undefined;
    }

    const pattern = new RegExp(`^[a-zA-Z0-9._%+-]+@(${allowedDomainsAll.join("|")})$`);
    const invalid = list.filter((entry) => !pattern.test(entry));
    if (invalid.length) {
      return `Permitted domains: ${allowedDomainsAll.join(", ")}`;
    }
    return undefined;
  };

  return (
    <Formik<SendFormValues>
      enableReinitialize
      initialValues={initialValues}
      validate={(values) => {
        const errors: Record<string, string> = {};
        const emailIdsError = validateList(values.emailIds, "Email ID(s)", true);
        if (emailIdsError) errors.emailIds = emailIdsError;

        const ccError = validateList(values.cc, "Cc", false);
        if (ccError) errors.cc = ccError;

        const bccError = validateList(values.bcc, "Bcc", false);
        if (bccError) errors.bcc = bccError;

        if (!values.subject.trim()) errors.subject = "Email Subject is required";
        if (!values.message.trim()) errors.message = "Email Message is required";
        return errors;
      }}
      onSubmit={(values, { resetForm }) => {
        /* It used to report success without sending anything. */
        setIsSending(true);
        onSend({
          to: listOf(values.emailIds),
          cc: listOf(values.cc),
          bcc: listOf(values.bcc),
          subject: values.subject.trim(),
          message: values.message.trim(),
        })
          .then(() => {
            resetForm();
            onSent();
          })
          .catch((error: any) =>
            openNotification({
              title: "Failed to send the document",
              message: error?.message ?? "Please try again later.",
              type: "error",
            })
          )
          .finally(() => setIsSending(false));
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values, touched, errors, resetForm, setFieldValue, setFieldTouched }) => {
        const err = (field: keyof SendFormValues) =>
          touched[field] && errors[field] ? (errors[field] as string) : undefined;

        /* Addresses as tags: type or paste, and Enter / , / ; / space makes each one a tag. */
        const recipients = (field: "emailIds" | "cc" | "bcc") => (
          <Select
            mode="tags"
            value={values[field]}
            tokenSeparators={[",", ";", " "]}
            notFoundContent={null}
            onChange={(next: string[]) => setFieldValue(field, listOf(next))}
            onBlur={() => setFieldTouched(field, true)}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          />
        );

        const close = () => {
          resetForm();
          onCancel();
        };

        return (
          <Modal
            open={open}
            centered
            maskClosable={false}
            width={560}
            onCancel={close}
            title={
              <Typography.Title
                level={3}
                style={{ textAlign: "center", margin: 0 }}
              >
                Send Document
              </Typography.Title>
            }
            footer={
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button
                  onClick={close}
                  disabled={isSending}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={isSending}
                  onClick={() => handleSubmit()}
                >
                  Send
                </Button>
              </div>
            }
          >
            <Form layout="vertical">
              <Form.Item required
                label="Email ID(s)"
                validateStatus={err("emailIds") ? "error" : undefined}
                help={err("emailIds")}
              >
                {recipients("emailIds")}
              </Form.Item>

              <Form.Item
                label="Cc"
                validateStatus={err("cc") ? "error" : undefined}
                help={err("cc")}
              >
                {recipients("cc")}
              </Form.Item>

              <Form.Item
                label="Bcc"
                validateStatus={err("bcc") ? "error" : undefined}
                help={err("bcc")}
              >
                {recipients("bcc")}
              </Form.Item>

              <Form.Item required
                label="Email Subject"
                validateStatus={err("subject") ? "error" : undefined}
                help={err("subject")}
              >
                <Input
                  name="subject"
                  value={values.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Form.Item>

              <Form.Item required
                label="Email Message"
                validateStatus={err("message") ? "error" : undefined}
                help={err("message")}
              >
                <Input.TextArea
                  name="message"
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={5}
                  showCount
                  maxLength={MESSAGE_LIMIT}
                />
              </Form.Item>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};
